import React, { useState } from "react";
import { MoreVertical, Eye, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CashInOutRecord } from "../../api/services/cashExchange/type";

interface ExtendedCashInOutRecord extends CashInOutRecord {
  customerId?: string;
}

interface CashRecordsTableProps {
  records: ExtendedCashInOutRecord[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  totals: { totalCashIn: number; totalCashOut: number };
  totalCount: number;
  selectedExchangeType?: string;
  onRecordSelect?: (record: ExtendedCashInOutRecord) => void;
  selectedRecordId?: string | null;
}

export const CashRecordsTable: React.FC<CashRecordsTableProps> = ({
  records,
  isLoading,
  isError,
  onRecordSelect,
  selectedRecordId,
}) => {
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Handle clicking outside dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".dropdown-container")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getTypeDisplay = (type: "cash-in" | "cash-out") => {
    if (type === "cash-in") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Cash In
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Cash Out
        </span>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading cash records...</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="text-red-400">Error loading records</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Shift
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                Amount
              </th>
              {/* Additional columns for cash-in records */}
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Payment Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Currency
              </th>
              <th className="px-6 py-4 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {records.map((record) => (
              <tr
                key={`${record.type}-${record.id}`}
                className={`
                  hover:bg-slate-700 transition-colors cursor-pointer
                  ${selectedRecordId === record.id ? "bg-slate-700/50 border-l-4 border-blue-500" : ""}
                `}
                onClick={(e) => {
                  // Don't trigger row click if clicking on action menu
                  if (
                    !(e.target as HTMLElement).closest(".dropdown-container")
                  ) {
                    onRecordSelect?.(record);
                  }
                }}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {formatDate(record.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {record.shift}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {getTypeDisplay(record.type)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-slate-300">
                  <span
                    className={`font-medium ${
                      record.type === "cash-in"
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                  >
                    {formatCurrency(record.amount)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {record.type === "cash-in" && record.paymentType
                    ? record.paymentType
                    : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {record.type === "cash-in" && record.currency
                    ? record.currency
                    : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center relative">
                  <div className="relative inline-block dropdown-container">
                    <button
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === record.id ? null : record.id,
                        )
                      }
                      className="text-slate-400 hover:text-white transition-colors p-1"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {activeDropdown === record.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-slate-700 rounded-lg shadow-lg py-1 z-10">
                        {record.customerId && (
                          <button
                            onClick={() => {
                              navigate(`/customers/${record.customerId}`);
                              setActiveDropdown(null);
                            }}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-600 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View Customer Details
                          </button>
                        )}
                        <button
                          onClick={() => {
                            window.open(
                              `/customers/${record.customerId}`,
                              "_blank",
                            );
                            setActiveDropdown(null);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-600 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open in New Tab
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {records.length === 0 && (
        <div className="text-center py-12">
          <div className="text-slate-400">No cash records found</div>
        </div>
      )}
    </div>
  );
};
